import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { scansApi, sourcesApi, cbomApi, sbomApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { ScanStatusBadge, StatusBadge, ModalityBadge, QuantumSafeBadge } from '@/components/common/StatusBadge'
import { ProgressBar } from '@/components/common/ProgressBar'
import { CryptoAssetsTable, FindingsSummaryBar } from '@/components/common/CryptoAssetsTable'
import { CertExpiryChips, CertExpiryBarChart, CertCaHierarchy } from '@/components/common/CertInventoryViews'
import { getDaysUntilExpiry } from '@/utils/certUtils'
import { useTheme } from '@/contexts/ThemeContext'
import { RiskBadge, SeverityBadge } from '@/pages/Packages'
import type { FindingEntry, FindingsSummary, PackageSummary, AssetType, Severity, QuantumRisk, PackageFinding } from '@/api/types'

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1">
      <dt className="text-sm text-subtle-light dark:text-subtle-dark w-40 shrink-0">{label}</dt>
      <dd className="text-sm text-text-light dark:text-text-dark">{value}</dd>
    </div>
  )
}

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function formatDuration(started?: string, completed?: string) {
  if (!started) return '—'
  const end = completed ? new Date(completed) : new Date()
  const secs = Math.floor((end.getTime() - new Date(started).getTime()) / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ${secs % 60}s`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

interface DownloadButtonProps {
  label: string
  icon: string
  onClick: () => Promise<void>
}

function DownloadButton({ label, icon, onClick }: DownloadButtonProps) {
  const mutation = useMutation({ mutationFn: onClick })
  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="flex items-center gap-2 px-4 py-2.5 text-sm bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] text-text-light dark:text-text-dark rounded-lg hover:bg-background-light dark:hover:bg-background-dark hover:border-primary hover:text-primary disabled:opacity-50 transition-colors"
    >
      {mutation.isPending ? (
        <span className="material-icons text-base animate-spin">sync</span>
      ) : (
        <span className="material-icons text-base">{icon}</span>
      )}
      {label}
    </button>
  )
}

// ── Asset filters ─────────────────────────────────────────────────────────────

const ASSET_TYPES: AssetType[] = ['algorithm', 'certificate', 'key', 'protocol', 'kdf']
// 'info' included: quantum-safe findings are reported at info severity, and
// omitting it made them invisible in the by-risk breakdown.
const SEVERITY_LEVELS: Severity[] = ['critical', 'high', 'medium', 'low', 'info']

interface AssetFilter {
  assetType: AssetType | '' | 'others'
  severity: Severity | ''
  quantumSafe: 'all' | 'safe' | 'unsafe'
  quantumRisk: QuantumRisk | ''
  search: string
  primitive: string
  usageContext: string
}

const PAGE_SIZE = 50

type BomTab = 'crypto' | 'packages'

const QUANTUM_RISKS: QuantumRisk[] = ['vulnerable', 'partial', 'hybrid', 'safe', 'unknown']
const PRIMITIVES = ['ae', 'dh', 'dsa', 'ec', 'ekdf', 'hash', 'kdf', 'mac', 'rsa', 'signature', 'xof']
const USAGE_CONTEXTS = ['transport', 'storage', 'signing', 'auth']


export default function ScanDetails() {
  const { id } = useParams<{ id: string }>()
  const { currentOrgId } = useOrgStore()
  const [filter, setFilter] = useState<AssetFilter>({
    assetType: '',
    severity: '',
    quantumSafe: 'all',
    quantumRisk: '',
    search: '',
    primitive: '',
    usageContext: '',
  })
  const [bomTab, setBomTab] = useState<BomTab>('crypto')
  const [certExpiredOnly, setCertExpiredOnly] = useState(false)
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [pkgSearch, setPkgSearch] = useState('')
  const [pkgQRisk, setPkgQRisk] = useState<QuantumRisk | ''>('')
  const [pkgEcosystem, setPkgEcosystem] = useState('')
  const [pkgScope, setPkgScope] = useState<'all' | 'direct' | 'transitive'>('all')
  const [pkgSafety, setPkgSafety] = useState<'all' | 'safe' | 'unsafe'>('all')
  const [pkgSeverity, setPkgSeverity] = useState<Severity | ''>('')
  const [expandedPkgId, setExpandedPkgId] = useState<string | null>(null)

  const { data: scanResp, isLoading: scanLoading } = useQuery({
    queryKey: ['scan', currentOrgId, id],
    queryFn: () => scansApi.get(currentOrgId, id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const scan = query.state.data?.data
      return scan?.status === 'running' || scan?.status === 'pending' ? 3000 : false
    },
  })

  const scan = scanResp?.data

  // Tick every second while scan is running to keep duration display live.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (scan?.status !== 'running' && scan?.status !== 'pending') return
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [scan?.status])

  const { data: sourceResp } = useQuery({
    queryKey: ['source', currentOrgId, scan?.source_id],
    queryFn: () => sourcesApi.get(currentOrgId, scan!.source_id),
    enabled: !!scan?.source_id,
  })

  const [cryptoPages, setCryptoPages] = useState<FindingEntry[][]>([])
  const [cryptoOffset, setCryptoOffset] = useState(0)
  const [cryptoHasMore, setCryptoHasMore] = useState(false)
  const [cryptoTotal, setCryptoTotal] = useState(0)
  const [summary, setSummary] = useState<FindingsSummary | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)

  useEffect(() => {
    setCryptoOffset(0)
    setCryptoHasMore(false)
  }, [filter.search, filter.assetType, filter.severity, filter.quantumSafe, filter.quantumRisk, filter.primitive, filter.usageContext])

  // Only fetch findings when the scan is completed
  const { data: findingsData, isLoading: findingsLoading, isFetching: findingsFetching } = useQuery({
    queryKey: ['scan-findings', currentOrgId, id, filter, cryptoOffset],
    queryFn: () => cbomApi.fetchScanFindings(currentOrgId, id!, {
      limit: PAGE_SIZE,
      offset: cryptoOffset,
      name: filter.search || undefined,
      asset_type: (filter.assetType === 'others' ? undefined : filter.assetType) || undefined,
      severity: filter.severity || undefined,
      is_quantum_safe: filter.quantumSafe === 'all' ? undefined : filter.quantumSafe === 'safe',
      quantum_risk: filter.quantumRisk || undefined,
      primitive: filter.primitive || undefined,
      usage_context: filter.usageContext || undefined,
    }),
    enabled: !!id && scan?.status === 'completed',
  })

  useEffect(() => {
    if (!findingsData) return
    const batch = findingsData.findings
    setCryptoPages((prev) => {
      if (cryptoOffset === 0) return [batch]
      const next = [...prev]
      next[cryptoOffset / PAGE_SIZE] = batch
      return next
    })
    setCryptoHasMore(cryptoOffset + batch.length < findingsData.total)
    setCryptoTotal(findingsData.total)
    if (cryptoOffset === 0) setSummary(findingsData.summary)
  }, [findingsData, cryptoOffset])

  useEffect(() => {
    if (filter.assetType !== 'others') return
    const cryptoFindings = cryptoPages.flat()
    const hasOthers = cryptoFindings.some(f => !ASSET_TYPES.includes(f.asset_type as AssetType))
    if (!hasOthers && cryptoHasMore && !findingsFetching) {
      setCryptoOffset(o => o + PAGE_SIZE)
    }
  }, [filter.assetType, cryptoPages, cryptoHasMore, findingsFetching])

  isFetchingRef.current = findingsFetching

  const loadMoreCrypto = useCallback(() => setCryptoOffset((p) => p + PAGE_SIZE), [])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && cryptoHasMore && !isFetchingRef.current) loadMoreCrypto()
      },
      { rootMargin: '600px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [cryptoHasMore, loadMoreCrypto])

  const [pkgPages, setPkgPages] = useState<PackageFinding[][]>([])
  const [pkgOffset, setPkgOffset] = useState(0)
  const [pkgHasMore, setPkgHasMore] = useState(false)
  const [pkgTotal, setPkgTotal] = useState(0)
  const [pkgSummary, setPkgSummary] = useState<PackageSummary | null>(null)
  const pkgSentinelRef = useRef<HTMLDivElement>(null)
  const pkgIsFetchingRef = useRef(false)

  useEffect(() => {
    setPkgOffset(0)
    setPkgHasMore(false)
  }, [pkgSearch, pkgQRisk, pkgEcosystem, pkgScope, pkgSafety])

  const { data: pkgData, isLoading: pkgLoading, isFetching: pkgFetching } = useQuery({
    queryKey: ['scan-pkg-findings', currentOrgId, id, pkgSearch, pkgQRisk, pkgEcosystem, pkgScope, pkgSafety, pkgOffset],
    queryFn: () => cbomApi.fetchScanPackageFindings(currentOrgId, id!, {
      limit: PAGE_SIZE,
      offset: pkgOffset,
      name: pkgSearch || undefined,
      quantum_risk: pkgQRisk || undefined,
      ecosystem: pkgEcosystem || undefined,
      scope: pkgScope === 'all' ? undefined : pkgScope,
      is_quantum_safe: pkgSafety === 'all' ? undefined : pkgSafety === 'safe',
    }),
    enabled: !!id && scan?.status === 'completed',
  })

  useEffect(() => {
    if (!pkgData) return
    const batch = pkgData.findings
    setPkgPages((prev) => {
      if (pkgOffset === 0) return [batch]
      const next = [...prev]
      next[pkgOffset / PAGE_SIZE] = batch
      return next
    })
    setPkgHasMore(pkgOffset + batch.length < pkgData.total)
    setPkgTotal(pkgData.total)
    if (pkgOffset === 0) setPkgSummary(pkgData.summary)
  }, [pkgData, pkgOffset])

  pkgIsFetchingRef.current = pkgFetching

  const loadMorePkg = useCallback(() => setPkgOffset((p) => p + PAGE_SIZE), [])

  useEffect(() => {
    const el = pkgSentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pkgHasMore && !pkgIsFetchingRef.current) loadMorePkg()
      },
      { rootMargin: '600px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [pkgHasMore, loadMorePkg, bomTab])

  if (scanLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-icons animate-spin text-primary text-4xl">sync</span>
      </div>
    )
  }

  if (!scan) {
    return (
      <div className="text-center py-20">
        <p className="text-subtle-light dark:text-subtle-dark">Scan not found.</p>
        <Link to="/scans" className="text-primary hover:underline text-sm mt-2 inline-block">
          ← Back to Scans
        </Link>
      </div>
    )
  }

  const source = sourceResp?.data

  const visiblePkgs = pkgPages.flat().filter(pkg => !pkgSeverity || pkg.severity === pkgSeverity)

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-subtle-light dark:text-subtle-dark">
        <Link to="/scans" className="hover:text-primary">
          Scan Jobs
        </Link>
        <span className="material-icons text-base">chevron_right</span>
        <code className="text-text-light dark:text-text-dark font-mono">{scan.id.slice(0, 8)}…</code>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-text-light dark:text-text-dark font-mono">
              {scan.id.slice(0, 8)}…
            </h1>
            <ScanStatusBadge status={scan.status} />
            <StatusBadge label={scan.trigger_type} variant="neutral" />
          </div>
          {source && (
            <p className="text-sm text-subtle-light dark:text-subtle-dark mt-1">
              Source:{' '}
              <Link to={`/sources/${source.id}`} className="text-primary hover:underline">
                {source.name}
              </Link>{' '}
              <ModalityBadge modality={source.modality} />
            </p>
          )}
        </div>

        {scan.status === 'completed' && (
          <div className="flex flex-wrap gap-2">
            <DownloadButton
              label="CBOM"
              icon="download"
              onClick={() => cbomApi.downloadScanCbom(currentOrgId, scan.id)}
            />
            <DownloadButton
              label="SBOM"
              icon="inventory_2"
              onClick={() => sbomApi.downloadScanSbom(currentOrgId, scan.id, 'cyclonedx')}
            />
            <DownloadButton
              label="Findings"
              icon="security"
              onClick={() => cbomApi.downloadScanFindings(currentOrgId, scan.id)}
            />
            <DownloadButton
              label="SARIF"
              icon="code"
              onClick={() => cbomApi.downloadScanSarif(currentOrgId, scan.id)}
            />
          </div>
        )}
      </div>

      {/* Scan metadata + progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6 space-y-4">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
            Scan Details
          </h2>

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-subtle-light dark:text-subtle-dark">Progress</span>
              <span className="text-sm text-text-light dark:text-text-dark">{scan.progress}%</span>
            </div>
            <ProgressBar value={scan.progress} animated={scan.status === 'running'} />
            {scan.status === 'running' && (() => {
              const stages = ['Initialise', 'Enumerate', 'Scan', 'Analyse', 'Report']
              const thresholds = [0, 20, 50, 80, 95]
              const currentStage = thresholds.reduce((acc, t, i) => scan.progress >= t ? i : acc, 0)
              return (
                <div className="flex items-start mt-3">
                  {stages.map((s, i) => (
                    <Fragment key={s}>
                      <div className="flex flex-col items-center gap-1 shrink-0">
                        <span className={`w-2.5 h-2.5 rounded-full ${
                          i < currentStage  ? 'bg-success'
                          : i === currentStage ? 'bg-primary dark:bg-secondary animate-pulse'
                          : 'bg-border-light dark:bg-border-dark'
                        }`} />
                        <span className="text-[10px] text-subtle-light dark:text-subtle-dark whitespace-nowrap">{s}</span>
                      </div>
                      {i < stages.length - 1 && (
                        <div className="flex-1 h-px bg-border-light dark:bg-border-dark mt-1.5 mx-1" />
                      )}
                    </Fragment>
                  ))}
                </div>
              )
            })()}
          </div>

          {scan.status === 'failed' && scan.error_message && (
            <div className="flex items-start gap-2 p-3 bg-error/10 dark:bg-error/15 rounded-xl">
              <span className="material-icons text-error text-base mt-0.5">error_outline</span>
              <div>
                <p className="text-sm font-medium text-error">Scan failed</p>
                <p className="text-xs text-error/70 mt-0.5 font-mono">
                  {scan.error_message}
                </p>
              </div>
            </div>
          )}

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
            <InfoRow label="Status" value={<ScanStatusBadge status={scan.status} />} />
            <InfoRow label="Trigger" value={<StatusBadge label={scan.trigger_type} variant="neutral" />} />
            <InfoRow label="Started" value={formatDate(scan.started_at)} />
            <InfoRow label="Completed" value={formatDate(scan.completed_at)} />
            <InfoRow label="Duration" value={formatDuration(scan.started_at, scan.completed_at)} />
            <InfoRow label="Scan ID" value={<code className="text-xs font-mono">{scan.id}</code>} />
          </dl>

          {scan.status === 'completed' && summary && (
            <div className="flex flex-wrap gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-container-light dark:bg-container-dark rounded-lg text-xs text-text-light dark:text-text-dark">
                <span className="material-icons text-[14px] text-primary dark:text-secondary">security</span>
                <span className="font-medium">{summary.total}</span>
                <span className="text-subtle-light dark:text-subtle-dark">assets</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-container-light dark:bg-container-dark rounded-lg text-xs text-text-light dark:text-text-dark">
                <span className="material-icons text-[14px] text-primary dark:text-secondary">category</span>
                <span className="font-medium">{pkgTotal}</span>
                <span className="text-subtle-light dark:text-subtle-dark">packages</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-container-light dark:bg-container-dark rounded-lg text-xs text-text-light dark:text-text-dark">
                <span className="material-icons text-[14px] text-primary dark:text-secondary">timer</span>
                <span className="font-medium">{formatDuration(scan.started_at, scan.completed_at)}</span>
              </span>
            </div>
          )}
        </div>

        {/* Summary stats */}
        <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
            Asset Summary
          </h2>
          {scan.status === 'completed' && summary ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-subtle-light dark:text-subtle-dark">Total assets</span>
                <span className="font-bold text-text-light dark:text-text-dark">{summary.total}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-subtle-light dark:text-subtle-dark">Quantum safe</span>
                <span className="font-bold text-success">{summary.quantum_safe}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-subtle-light dark:text-subtle-dark">At risk</span>
                <span className="font-bold text-error">{summary.quantum_unsafe}</span>
              </div>

              <div className="border-t border-border-light dark:border-border-dark pt-3 space-y-1">
                <p className="text-xs text-subtle-light dark:text-subtle-dark mb-2">By risk level</p>
                {SEVERITY_LEVELS.filter((r) => summary.by_risk[r]).map((r) => (
                  <div key={r} className="flex items-center justify-between text-xs">
                    <span className="capitalize text-subtle-light dark:text-subtle-dark">{r}</span>
                    <span className="font-medium text-text-light dark:text-text-dark">
                      {summary.by_risk[r] ?? 0}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-border-light dark:border-border-dark pt-3 space-y-1">
                <p className="text-xs text-subtle-light dark:text-subtle-dark mb-2">By asset type</p>
                {ASSET_TYPES.filter((t) => summary.by_asset_type[t]).map((t) => (
                  <div key={t} className="flex items-center justify-between text-xs">
                    <span className="capitalize text-subtle-light dark:text-subtle-dark">{t}</span>
                    <span className="font-medium text-text-light dark:text-text-dark">
                      {summary.by_asset_type[t] ?? 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : scan.status === 'running' ? (
            <div className="text-center py-8">
              <span className="material-icons text-3xl text-primary animate-spin">sync</span>
              <p className="text-sm text-subtle-light dark:text-subtle-dark mt-2">Scanning…</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <span className="material-icons text-3xl opacity-40 text-subtle-light dark:text-subtle-dark">
                key
              </span>
              <p className="text-sm text-subtle-light dark:text-subtle-dark mt-2">
                {scan.status === 'failed' ? 'Scan did not complete.' : 'Awaiting scan start.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Discovered assets — only shown when completed */}
      {scan.status === 'completed' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark">
            Discovered Assets
          </h2>

          {/* Tab bar */}
          <div className="border-b border-border-light dark:border-border-dark">
            <div className="flex gap-1">
              {([
                ['crypto', 'Crypto Assets', summary?.quantum_safe, summary?.total],
                ['packages', 'Packages', pkgSummary ? (pkgSummary.by_quantum_risk['safe'] ?? 0) : undefined, pkgTotal || undefined],
              ] as [BomTab, string, number | undefined, number | undefined][]).map(([t, label, safe, total]) => (
                <button
                  key={t}
                  onClick={() => setBomTab(t)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    bomTab === t
                      ? 'border-primary text-primary'
                      : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
                  }`}
                >
                  {label}
                  {safe !== undefined && total !== undefined && (
                    <span className="ml-1.5 text-xs opacity-70">
                      ({safe} of {total})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ── Crypto Assets tab ── */}
          {bomTab === 'crypto' && (() => {
            const isCertView = filter.assetType === 'certificate'
            const baseFindings = filter.assetType === 'others'
              ? cryptoPages.flat().filter(f => !ASSET_TYPES.includes(f.asset_type as AssetType))
              : cryptoPages.flat()
            const displayFindings = isCertView && certExpiredOnly
              ? baseFindings.filter(f => { const d = getDaysUntilExpiry(f); return d !== null && d <= 0 })
              : baseFindings
            return (
              <>
                {summary && (
                  <FindingsSummaryBar
                    total={summary.total}
                    quantumSafe={summary.quantum_safe}
                    quantumUnsafe={summary.quantum_unsafe}
                    byRisk={summary.by_risk}
                    byLifecycle={summary.by_lifecycle}
                  />
                )}

                {/* Certificate expiry chips + bar chart */}
                {isCertView && baseFindings.length > 0 && (
                  <>
                    <CertExpiryChips findings={baseFindings} />
                    <CertExpiryBarChart findings={baseFindings} isDark={isDark} />
                  </>
                )}

                <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
                  <div className="flex flex-wrap gap-3">
                    <input
                      type="text"
                      placeholder="Search by name…"
                      value={filter.search}
                      onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                      className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-52 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <select
                      value={filter.assetType}
                      onChange={(e) => { setFilter({ ...filter, assetType: e.target.value as AssetType | '' | 'others' }); setCertExpiredOnly(false) }}
                      className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                    >
                      <option value="">All Types</option>
                      {ASSET_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                      <option value="others">others</option>
                    </select>
                    <select
                      value={filter.severity}
                      onChange={(e) => setFilter({ ...filter, severity: e.target.value as Severity | '' })}
                      className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                    >
                      <option value="">All Severities</option>
                      {SEVERITY_LEVELS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <select
                      value={filter.quantumSafe}
                      onChange={(e) =>
                        setFilter({ ...filter, quantumSafe: e.target.value as 'all' | 'safe' | 'unsafe' })
                      }
                      className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                    >
                      <option value="all">All Quantum Status</option>
                      <option value="safe">Quantum Safe</option>
                      <option value="unsafe">At Risk</option>
                    </select>
                    <select
                      value={filter.quantumRisk}
                      onChange={(e) => setFilter({ ...filter, quantumRisk: e.target.value as QuantumRisk | '' })}
                      className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                    >
                      <option value="">All Quantum Risk</option>
                      {QUANTUM_RISKS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <select
                      value={filter.primitive}
                      onChange={(e) => setFilter({ ...filter, primitive: e.target.value })}
                      className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                    >
                      <option value="">All Primitives</option>
                      {PRIMITIVES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <select
                      value={filter.usageContext}
                      onChange={(e) => setFilter({ ...filter, usageContext: e.target.value })}
                      className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                    >
                      <option value="">All Usage Contexts</option>
                      {USAGE_CONTEXTS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {isCertView && (
                      <label className="flex items-center gap-2 px-3 py-2 text-sm text-text-light dark:text-text-dark cursor-pointer">
                        <input
                          type="checkbox"
                          checked={certExpiredOnly}
                          onChange={(e) => setCertExpiredOnly(e.target.checked)}
                          className="rounded border-border-light dark:border-border-dark accent-error"
                        />
                        Expired only
                      </label>
                    )}
                  </div>
                </div>
                <CryptoAssetsTable
                  findings={displayFindings}
                  isLoading={findingsLoading && cryptoPages.length === 0}
                  emptyMessage={
                    cryptoTotal === 0
                      ? 'No crypto assets were discovered in this scan.'
                      : 'No assets match the current filters.'
                  }
                />
                <div ref={sentinelRef} className="h-1" />
                {findingsFetching && cryptoOffset > 0 && (
                  <div className="flex items-center justify-center py-4">
                    <span className="material-icons animate-spin text-primary text-2xl">sync</span>
                  </div>
                )}

                {/* CA hierarchy — only in cert view, after all pages loaded */}
                {isCertView && !cryptoHasMore && !findingsLoading && baseFindings.length > 0 && (
                  <CertCaHierarchy findings={baseFindings} />
                )}
              </>
            )
          })()}

          {/* ── Packages tab ── */}
          {bomTab === 'packages' && (
            <>
              {/* Package summary blocks */}
              {pkgSummary && (() => {
                const s = pkgSummary
                const safeCount = s.by_quantum_risk['safe'] ?? 0
                const atRisk = s.total - safeCount
                const highCritical = s.by_quantum_risk['vulnerable'] ?? 0
                const safePercent = s.total > 0 ? Math.round((safeCount / s.total) * 100) : 0
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
                      <p className="label-sm text-subtle-light dark:text-subtle-dark">Total Packages</p>
                      <p className="text-2xl font-bold tracking-display text-text-light dark:text-text-dark mt-1">{s.total}</p>
                    </div>
                    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
                      <p className="label-sm text-subtle-light dark:text-subtle-dark">Quantum Safe</p>
                      <p className="text-2xl font-bold tracking-display text-success mt-1">
                        {safeCount}
                        <span className="text-sm font-normal text-subtle-light dark:text-subtle-dark ml-1">({safePercent}%)</span>
                      </p>
                    </div>
                    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
                      <p className="label-sm text-subtle-light dark:text-subtle-dark">At Risk</p>
                      <p className="text-2xl font-bold tracking-display text-error mt-1">{atRisk}</p>
                    </div>
                    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
                      <p className="label-sm text-subtle-light dark:text-subtle-dark">Vulnerable</p>
                      <p className="text-2xl font-bold tracking-display text-warning mt-1">{highCritical}</p>
                    </div>
                  </div>
                )
              })()}

              {/* Package filters */}
              <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
                <div className="flex flex-wrap gap-3">
                  <input
                    type="text"
                    placeholder="Search by name or PURL…"
                    value={pkgSearch}
                    onChange={(e) => setPkgSearch(e.target.value)}
                    className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-60 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <select
                    value={pkgQRisk}
                    onChange={(e) => setPkgQRisk(e.target.value as QuantumRisk | '')}
                    className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                  >
                    <option value="">All Quantum Risk</option>
                    {QUANTUM_RISKS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <select
                    value={pkgSeverity}
                    onChange={(e) => setPkgSeverity(e.target.value as Severity | '')}
                    className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                  >
                    <option value="">All Severities</option>
                    {(['critical', 'high', 'medium', 'low', 'info'] as Severity[]).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Ecosystem…"
                    value={pkgEcosystem}
                    onChange={(e) => setPkgEcosystem(e.target.value)}
                    className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-36 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <select
                    value={pkgScope}
                    onChange={(e) => setPkgScope(e.target.value as 'all' | 'direct' | 'transitive')}
                    className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                  >
                    <option value="all">All Scope</option>
                    <option value="direct">Direct</option>
                    <option value="transitive">Transitive</option>
                  </select>
                  <select
                    value={pkgSafety}
                    onChange={(e) => setPkgSafety(e.target.value as 'all' | 'safe' | 'unsafe')}
                    className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                  >
                    <option value="all">All Quantum Status</option>
                    <option value="safe">Safe</option>
                    <option value="unsafe">At Risk</option>
                  </select>
                </div>
              </div>

              {/* Package table */}
              {pkgLoading && pkgPages.length === 0 && (
                <div className="flex items-center justify-center h-32">
                  <span className="material-icons animate-spin text-primary text-3xl">sync</span>
                </div>
              )}
              {!pkgFetching && pkgPages.length > 0 && visiblePkgs.length === 0 && (
                <div className="text-center py-12">
                  <span className="material-icons text-3xl text-subtle-light dark:text-subtle-dark opacity-40">
                    inventory_2
                  </span>
                  <p className="text-sm text-subtle-light dark:text-subtle-dark mt-2">
                    {pkgTotal === 0 ? 'No packages found for this scan.' : 'No packages match the current filters.'}
                  </p>
                </div>
              )}
              {visiblePkgs.length > 0 && (
                <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-light dark:border-border-dark">
                        <th className="w-8 px-3 py-3" />
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Package</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Version</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Ecosystem</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Modality</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Scope</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Severity</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Quantum Risk</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Quantum Safety</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Evidence</th>
                        <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden lg:table-cell">Licence</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light dark:divide-border-dark">
                      {visiblePkgs.map((pkg, i) => (
                        <Fragment key={pkg.id}>
                          <tr
                            className={`cursor-pointer transition-colors hover:bg-background-light dark:hover:bg-background-dark ${i % 2 === 1 ? 'bg-background-light/50 dark:bg-background-dark/50' : ''}`}
                            onClick={() => setExpandedPkgId(prev => prev === pkg.id ? null : pkg.id)}
                          >
                            <td className="px-3 py-3 text-center">
                              <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">
                                {expandedPkgId === pkg.id ? 'expand_less' : 'expand_more'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-text-light dark:text-text-dark truncate max-w-[200px]">{pkg.name}</p>
                              {pkg.purl && (
                                <p className="font-mono text-xs text-subtle-light dark:text-subtle-dark truncate max-w-[200px]" title={pkg.purl}>
                                  {pkg.purl}
                                </p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark hidden sm:table-cell">
                              {pkg.version || '—'}
                            </td>
                            <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark hidden md:table-cell">
                              {pkg.ecosystem || '—'}
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              {pkg.modality ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                                  pkg.modality === 'binary'  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                  pkg.modality === 'network' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                               'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark'
                                }`}>
                                  {pkg.modality}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                                pkg.is_direct
                                  ? 'bg-primary/10 dark:bg-secondary/15 text-primary dark:text-secondary'
                                  : 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark'
                              }`}>
                                {pkg.is_direct ? 'Direct' : 'Transitive'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <SeverityBadge level={pkg.severity} />
                            </td>
                            <td className="px-4 py-3">
                              <RiskBadge risk={pkg.quantum_risk} />
                            </td>
                            <td className="px-4 py-3">
                              <QuantumSafeBadge isSafe={pkg.is_quantum_safe} />
                            </td>
                            <td className="px-4 py-3 text-xs text-subtle-light dark:text-subtle-dark hidden md:table-cell max-w-[200px]">
                              <div className="flex flex-col gap-1">
                                {pkg.linked_assets?.length
                                  ? <span className="font-mono" title={pkg.linked_assets.map(e => e.name).join(', ')}>{pkg.linked_assets.length} crypto link{pkg.linked_assets.length !== 1 ? 's' : ''}</span>
                                  : null}
                                {pkg.evidence?.occurrences?.length
                                  ? <>
                                      <span className="font-mono truncate block" title={pkg.evidence.occurrences[0].location}>{pkg.evidence.occurrences[0].location}</span>
                                    </>
                                  : null}
                                {!pkg.linked_assets?.length && !pkg.evidence?.occurrences?.length ? '—' : null}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark text-xs hidden lg:table-cell truncate max-w-[160px]" title={pkg.declared_license ?? undefined}>
                              {pkg.declared_license || '—'}
                            </td>
                          </tr>
                          {expandedPkgId === pkg.id && (
                            <tr className="bg-container-light dark:bg-container-dark">
                              <td />
                              <td colSpan={9} className="px-4 py-4">
                                <div className="space-y-4">
                                  {pkg.linked_assets?.length ? (
                                    <div>
                                      <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-2">Crypto Links ({pkg.linked_assets.length})</p>
                                      <div className="space-y-1">
                                        {pkg.linked_assets.map((e) => (
                                          <div key={e.id} className="flex items-center gap-3 text-xs bg-background-light dark:bg-background-dark rounded px-3 py-2">
                                            <span className="font-medium text-text-light dark:text-text-dark">{e.name}</span>
                                            <span className="text-subtle-light dark:text-subtle-dark">{e.asset_type}</span>
                                            <RiskBadge risk={e.quantum_risk} />
                                            <QuantumSafeBadge isSafe={e.is_quantum_safe} />
                                            {e.confidence && <span className="font-mono text-subtle-light dark:text-subtle-dark">conf: {e.confidence}</span>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                  {pkg.evidence?.occurrences?.length ? (
                                    <div>
                                      <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-2">File Locations ({pkg.evidence.occurrences.length})</p>
                                      <ul className="space-y-1">
                                        {pkg.evidence.occurrences.map((o, j) => (
                                          <li key={j} className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-3 py-1.5">
                                            <span className="text-primary">{o.location}</span>
                                            {o.additionalContext && (
                                              <span className="block text-subtle-light dark:text-subtle-dark mt-0.5 break-all">{o.additionalContext}</span>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : null}
                                  {!pkg.linked_assets?.length && !pkg.evidence?.occurrences?.length && (
                                    <p className="text-xs text-subtle-light dark:text-subtle-dark">No evidence available.</p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div ref={pkgSentinelRef} className="h-1" />
              {pkgFetching && pkgOffset > 0 && (
                <div className="flex items-center justify-center py-4">
                  <span className="material-icons animate-spin text-primary text-2xl">sync</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
